import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  ClipboardList,
  Calculator,
  Users,
  Package,
  FileText,
  BarChart3,
  Calendar,
  DollarSign,
  Settings,
  ChevronDown,
  ChevronRight,
  Plus,
  Search,
  Filter,
  Eye,
  EyeOff,
  AlertTriangle,
  Clock,
  CheckCircle,
  TrendingUp,
  Layers,
  Wrench,
  Send,
  Target,
  Percent,
  Building,
  Bell,
  Mail,
  Camera,
  RefreshCw,
  Zap,
  ArrowRight,
  Link2,
  Trash2,
  Edit2,
  X,
  Save,
  Link,
  Unlink,
  FileDown,
  FolderOpen,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';

// Bid Types
const BID_TYPES = [
  { id: 'time_material', name: 'Time & Material', description: 'Bill based on actual time and materials used' },
  { id: 'fixed_price', name: 'Fixed Price', description: 'Set price regardless of actual costs' }
];

// Dependency Types
const DEPENDENCY_TYPES = [
  { id: 'FS', name: 'Finish-to-Start', description: 'Task starts after predecessor finishes' },
  { id: 'SS', name: 'Start-to-Start', description: 'Tasks start together' },
  { id: 'FF', name: 'Finish-to-Finish', description: 'Tasks finish together' },
  { id: 'SF', name: 'Start-to-Finish', description: 'Task finishes when predecessor starts' }
];

export const ProjectManagement = () => {
  const { api, isCOO, user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Tasks & Gantt State
  const [tasks, setTasks] = useState([]);
  const [ganttData, setGanttData] = useState(null);
  const [ganttViewMode, setGanttViewMode] = useState('detailed');
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({
    name: '',
    description: '',
    estimated_hours: 8,
    start_date: '',
    end_date: '',
    dependencies: [],
    is_milestone: false
  });
  
  // Daily Updates State
  const [dailyUpdates, setDailyUpdates] = useState([]);
  const [loadingUpdates, setLoadingUpdates] = useState(false);
  
  // Alert Settings State
  const [alertSettings, setAlertSettings] = useState(null);
  const [alertRecipients, setAlertRecipients] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // Bid Calculator State
  const [bidType, setBidType] = useState('fixed_price');
  const [bidData, setBidData] = useState({
    laborRates: { lead: 75, journeyman: 55, apprentice: 35 },
    materialMarkup: 20,
    overheadPercent: 15,
    profitMargin: 20,
    contingency: 10
  });
  const [useDbRates, setUseDbRates] = useState(false);
  const [laborLineItems, setLaborLineItems] = useState([
    { id: 1, description: 'Project Lead', role: 'lead', hours: 0, rate: 75 },
    { id: 2, description: 'Skilled Labor', role: 'journeyman', hours: 0, rate: 55 },
    { id: 3, description: 'Apprentice/Helper', role: 'apprentice', hours: 0, rate: 35 }
  ]);
  const [additionalCosts, setAdditionalCosts] = useState([
    { id: 1, description: 'Permits & Fees', cost: 0 },
    { id: 2, description: 'Equipment Rental', cost: 0 },
    { id: 3, description: 'Subcontractors', cost: 0 },
    { id: 4, description: 'Dumpster/Disposal', cost: 0 }
  ]);

  // Bid Templates State
  const [bidTemplates, setBidTemplates] = useState([]);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showLoadTemplateModal, setShowLoadTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateProjectType, setNewTemplateProjectType] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Visibility Toggles
  const [visibilitySettings, setVisibilitySettings] = useState({
    walter: {
      canViewBids: false,
      canViewFinancials: false,
      canViewSchedule: true,
      canViewTeam: true,
      canViewBOM: false
    },
    teamLeads: {
      canViewSchedule: true,
      canViewTeam: true,
      canViewTasks: true,
      canSubmitUpdates: true
    }
  });

  // Financials & Expense State
  const [financials, setFinancials] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddRevenue, setShowAddRevenue] = useState(false);
  const [newExpense, setNewExpense] = useState({
    category: 'misc_materials',
    description: '',
    amount: '',
    vendor: '',
    expense_date: new Date().toISOString().split('T')[0],
    notes: '',
    is_billable: true,
    tax_deductible: true,
    receipt_photos: []
  });
  const [newRevenue, setNewRevenue] = useState({
    description: '',
    amount: '',
    payment_type: 'progress_payment',
    payment_date: new Date().toISOString().split('T')[0],
    invoice_number: '',
    notes: ''
  });
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  // BOM State
  const [bomItems, setBomItems] = useState([]);
  const [bomCategories, setBomCategories] = useState([]);
  const [showAddBomItem, setShowAddBomItem] = useState(false);
  const [newBomItem, setNewBomItem] = useState({
    name: '',
    description: '',
    category: 'misc',
    quantity: 1,
    unit: 'each',
    unit_cost: 0,
    vendor: '',
    part_number: '',
    notes: ''
  });

  // SOW/SOV State
  const [sowData, setSowData] = useState({ sections: [] });
  const [sovData, setSovData] = useState({ line_items: [] });
  const [showAddSowSection, setShowAddSowSection] = useState(false);
  const [showAddSovItem, setShowAddSovItem] = useState(false);
  const [newSowSection, setNewSowSection] = useState({ title: '', content: '' });
  const [newSovItem, setNewSovItem] = useState({ description: '', scheduled_value: 0 });

  // Team Resources State
  const [teamResources, setTeamResources] = useState({ team: [], allocations: [], equipment: [] });
  const [showAllocateResource, setShowAllocateResource] = useState(false);
  const [newAllocation, setNewAllocation] = useState({
    employee_id: '',
    start_date: '',
    end_date: '',
    hours_per_day: 8
  });
  const [employeeRates, setEmployeeRates] = useState(null);

  // Dependency Management State
  const [showAddDependency, setShowAddDependency] = useState(false);
  const [selectedTaskForDep, setSelectedTaskForDep] = useState(null);
  const [newDependency, setNewDependency] = useState({
    predecessor_id: '',
    type: 'FS',
    lag_days: 0
  });
  const [editingTask, setEditingTask] = useState(null);

  // Redirect if not COO
  useEffect(() => {
    if (!isCOO) {
      toast.error('Access denied. COO access required.');
      navigate('/portal');
    }
  }, [isCOO, navigate]);

  // Fetch jobs
  useEffect(() => {
    fetchJobs();
    fetchEmployees();
  }, []);

  // Fetch job-specific data when selected
  useEffect(() => {
    if (selectedJob) {
      fetchTasks(selectedJob.id);
      fetchGanttData(selectedJob.id);
      fetchDailyUpdates(selectedJob.id);
      fetchAlertSettings(selectedJob.id);
      fetchFinancials(selectedJob.id);
      fetchExpenses(selectedJob.id);
      fetchRevenue(selectedJob.id);
      fetchBomItems(selectedJob.id);
      fetchSowData(selectedJob.id);
      fetchSovData(selectedJob.id);
      fetchTeamResources(selectedJob.id);
    }
  }, [selectedJob]);

  // Fetch expense categories and BOM categories once
  useEffect(() => {
    fetchExpenseCategories();
    fetchBomCategories();
    fetchEmployeeRates();
    fetchBidTemplates();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await api.get('/jobs');
      setJobs(response.data);
      if (response.data.length > 0 && !selectedJob) {
        setSelectedJob(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchTasks = async (jobId) => {
    try {
      const response = await api.get(`/project-management/jobs/${jobId}/tasks`);
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchGanttData = async (jobId) => {
    try {
      const response = await api.get(`/project-management/jobs/${jobId}/gantt`);
      setGanttData(response.data);
    } catch (error) {
      console.error('Error fetching gantt data:', error);
    }
  };

  const fetchDailyUpdates = async (jobId) => {
    setLoadingUpdates(true);
    try {
      const response = await api.get(`/project-management/jobs/${jobId}/daily-updates`);
      setDailyUpdates(response.data.updates || []);
    } catch (error) {
      console.error('Error fetching daily updates:', error);
    } finally {
      setLoadingUpdates(false);
    }
  };

  const fetchAlertSettings = async (jobId) => {
    try {
      const response = await api.get(`/project-management/jobs/${jobId}/alert-settings`);
      if (response.data.settings) {
        setAlertSettings(response.data.settings);
        setAlertRecipients(response.data.settings.recipients || []);
      } else {
        setAlertSettings(null);
        setAlertRecipients([]);
      }
    } catch (error) {
      console.error('Error fetching alert settings:', error);
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      const response = await api.get('/project-management/expense-categories');
      setExpenseCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching expense categories:', error);
    }
  };

  const fetchFinancials = async (jobId) => {
    try {
      const response = await api.get(`/project-management/jobs/${jobId}/financials`);
      setFinancials(response.data);
    } catch (error) {
      console.error('Error fetching financials:', error);
    }
  };

  const fetchExpenses = async (jobId) => {
    try {
      const response = await api.get(`/project-management/jobs/${jobId}/expenses`);
      setExpenses(response.data.expenses || []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const fetchRevenue = async (jobId) => {
    try {
      const response = await api.get(`/project-management/jobs/${jobId}/revenue`);
      setRevenue(response.data.revenue || []);
    } catch (error) {
      console.error('Error fetching revenue:', error);
    }
  };

  const handleReceiptUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setNewExpense(prev => ({
          ...prev,
          receipt_photos: [...prev.receipt_photos, event.target.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const submitExpense = async () => {
    if (!newExpense.description || !newExpense.amount) {
      toast.error('Description and amount are required');
      return;
    }

    try {
      await api.post(`/project-management/jobs/${selectedJob.id}/expenses`, {
        job_id: selectedJob.id,
        category: newExpense.category,
        description: newExpense.description,
        amount: parseFloat(newExpense.amount),
        vendor: newExpense.vendor,
        expense_date: newExpense.expense_date,
        notes: newExpense.notes,
        is_billable: newExpense.is_billable,
        tax_deductible: newExpense.tax_deductible,
        receipt_photos: newExpense.receipt_photos
      });
      toast.success('Expense submitted');
      setShowAddExpense(false);
      setNewExpense({
        category: 'misc_materials',
        description: '',
        amount: '',
        vendor: '',
        expense_date: new Date().toISOString().split('T')[0],
        notes: '',
        is_billable: true,
        tax_deductible: true,
        receipt_photos: []
      });
      fetchExpenses(selectedJob.id);
      fetchFinancials(selectedJob.id);
    } catch (error) {
      toast.error('Failed to submit expense');
    }
  };

  const submitRevenue = async () => {
    if (!newRevenue.description || !newRevenue.amount) {
      toast.error('Description and amount are required');
      return;
    }

    try {
      await api.post(`/project-management/jobs/${selectedJob.id}/revenue`, {
        job_id: selectedJob.id,
        description: newRevenue.description,
        amount: parseFloat(newRevenue.amount),
        payment_type: newRevenue.payment_type,
        payment_date: newRevenue.payment_date,
        invoice_number: newRevenue.invoice_number,
        notes: newRevenue.notes
      });
      toast.success('Revenue recorded');
      setShowAddRevenue(false);
      setNewRevenue({
        description: '',
        amount: '',
        payment_type: 'progress_payment',
        payment_date: new Date().toISOString().split('T')[0],
        invoice_number: '',
        notes: ''
      });
      fetchRevenue(selectedJob.id);
      fetchFinancials(selectedJob.id);
    } catch (error) {
      toast.error('Failed to record revenue');
    }
  };

  const approveExpense = async (expenseId, approved) => {
    try {
      await api.patch(`/project-management/expenses/${expenseId}/approve`, { approved });
      toast.success(`Expense ${approved ? 'approved' : 'rejected'}`);
      fetchExpenses(selectedJob.id);
      fetchFinancials(selectedJob.id);
    } catch (error) {
      toast.error('Failed to update expense');
    }
  };

  const createTask = async () => {
    if (!newTask.name) {
      toast.error('Task name is required');
      return;
    }
    
    try {
      await api.post(`/project-management/jobs/${selectedJob.id}/tasks`, {
        job_id: selectedJob.id,
        ...newTask
      });
      toast.success('Task created');
      setShowAddTask(false);
      setNewTask({
        name: '',
        description: '',
        estimated_hours: 8,
        start_date: '',
        end_date: '',
        dependencies: [],
        is_milestone: false
      });
      fetchTasks(selectedJob.id);
      fetchGanttData(selectedJob.id);
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const updateTaskStatus = async (taskId, status, progressPercent = null) => {
    try {
      const updates = { status };
      if (progressPercent !== null) {
        updates.progress_percent = progressPercent;
      }
      await api.patch(`/project-management/tasks/${taskId}`, updates);
      toast.success('Task updated');
      fetchTasks(selectedJob.id);
      fetchGanttData(selectedJob.id);
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const saveAlertSettings = async () => {
    if (!selectedJob) return;
    
    try {
      await api.post(`/project-management/jobs/${selectedJob.id}/alert-settings`, {
        job_id: selectedJob.id,
        daily_update_reminder: true,
        reminder_time: '17:00',
        send_email: true,
        send_in_app: true,
        recipients: alertRecipients
      });
      toast.success('Alert settings saved');
      fetchAlertSettings(selectedJob.id);
    } catch (error) {
      toast.error('Failed to save alert settings');
    }
  };

  const sendUpdateReminder = async () => {
    if (!selectedJob) return;
    
    try {
      const response = await api.post(`/project-management/jobs/${selectedJob.id}/send-update-reminder`);
      toast.success(`Reminders sent: ${response.data.notifications_sent} in-app, ${response.data.emails_sent} emails`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    }
  };

  // ==================== BOM Functions ====================
  const fetchBomCategories = async () => {
    try {
      const response = await api.get('/project-management/bom-categories');
      setBomCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching BOM categories:', error);
    }
  };

  const fetchBomItems = async (jobId) => {
    try {
      const response = await api.get(`/project-management/jobs/${jobId}/bom`);
      setBomItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching BOM items:', error);
    }
  };

  const addBomItem = async () => {
    if (!newBomItem.name || !newBomItem.unit_cost) {
      toast.error('Name and unit cost are required');
      return;
    }
    try {
      await api.post(`/project-management/jobs/${selectedJob.id}/bom`, newBomItem);
      toast.success('Material added to BOM');
      setShowAddBomItem(false);
      setNewBomItem({
        name: '', description: '', category: 'misc', quantity: 1,
        unit: 'each', unit_cost: 0, vendor: '', part_number: '', notes: ''
      });
      fetchBomItems(selectedJob.id);
    } catch (error) {
      toast.error('Failed to add material');
    }
  };

  const deleteBomItem = async (itemId) => {
    try {
      await api.delete(`/project-management/bom/${itemId}`);
      toast.success('Material removed');
      fetchBomItems(selectedJob.id);
    } catch (error) {
      toast.error('Failed to remove material');
    }
  };

  const updateBomStatus = async (itemId, status) => {
    try {
      await api.patch(`/project-management/bom/${itemId}`, { status });
      toast.success('Status updated');
      fetchBomItems(selectedJob.id);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // ==================== SOW/SOV Functions ====================
  const fetchSowData = async (jobId) => {
    try {
      const response = await api.get(`/project-management/jobs/${jobId}/sow`);
      setSowData(response.data || { sections: [] });
    } catch (error) {
      console.error('Error fetching SOW:', error);
    }
  };

  const fetchSovData = async (jobId) => {
    try {
      const response = await api.get(`/project-management/jobs/${jobId}/sov`);
      setSovData(response.data || { line_items: [] });
    } catch (error) {
      console.error('Error fetching SOV:', error);
    }
  };

  const saveSow = async () => {
    try {
      await api.post(`/project-management/jobs/${selectedJob.id}/sow`, sowData);
      toast.success('Scope of Work saved');
    } catch (error) {
      toast.error('Failed to save SOW');
    }
  };

  const addSowSection = () => {
    if (!newSowSection.title) {
      toast.error('Section title is required');
      return;
    }
    setSowData(prev => ({
      ...prev,
      sections: [...(prev.sections || []), { ...newSowSection, order: (prev.sections?.length || 0) }]
    }));
    setShowAddSowSection(false);
    setNewSowSection({ title: '', content: '' });
  };

  const saveSov = async () => {
    try {
      await api.post(`/project-management/jobs/${selectedJob.id}/sov`, sovData);
      toast.success('Schedule of Values saved');
      fetchSovData(selectedJob.id);
    } catch (error) {
      toast.error('Failed to save SOV');
    }
  };

  const addSovItem = () => {
    if (!newSovItem.description || !newSovItem.scheduled_value) {
      toast.error('Description and scheduled value are required');
      return;
    }
    setSovData(prev => ({
      ...prev,
      line_items: [...(prev.line_items || []), { 
        ...newSovItem, 
        id: `temp-${Date.now()}`,
        completed_value: 0,
        percent_complete: 0,
        retainage: 0,
        order: (prev.line_items?.length || 0) 
      }]
    }));
    setShowAddSovItem(false);
    setNewSovItem({ description: '', scheduled_value: 0 });
  };

  const updateSovProgress = async (itemId, percentComplete) => {
    try {
      await api.patch(`/project-management/jobs/${selectedJob.id}/sov/line-item/${itemId}`, {
        percent_complete: percentComplete
      });
      toast.success('Progress updated');
      fetchSovData(selectedJob.id);
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  // ==================== Team Resources Functions ====================
  const fetchTeamResources = async (jobId) => {
    try {
      const response = await api.get(`/project-management/jobs/${jobId}/team-resources`);
      setTeamResources(response.data || { team: [], allocations: [], equipment: [] });
    } catch (error) {
      console.error('Error fetching team resources:', error);
    }
  };

  const fetchEmployeeRates = async () => {
    try {
      const response = await api.get('/project-management/employees/rates');
      setEmployeeRates(response.data);
    } catch (error) {
      console.error('Error fetching employee rates:', error);
    }
  };

  const syncRatesFromDb = () => {
    if (employeeRates?.average_rates) {
      setBidData(prev => ({
        ...prev,
        laborRates: {
          lead: Math.round(employeeRates.average_rates.lead) || 75,
          journeyman: Math.round(employeeRates.average_rates.journeyman) || 55,
          apprentice: Math.round(employeeRates.average_rates.apprentice) || 35
        }
      }));
      setLaborLineItems(prev => prev.map(item => ({
        ...item,
        rate: Math.round(employeeRates.average_rates[item.role]) || item.rate
      })));
      toast.success('Rates synced from employee database');
    }
  };

  const addLaborLineItem = () => {
    setLaborLineItems(prev => [
      ...prev,
      { id: Date.now(), description: 'Custom Labor', role: 'journeyman', hours: 0, rate: bidData.laborRates.journeyman }
    ]);
  };

  const removeLaborLineItem = (id) => {
    setLaborLineItems(prev => prev.filter(item => item.id !== id));
  };

  const updateLaborLineItem = (id, field, value) => {
    setLaborLineItems(prev => prev.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const addAdditionalCost = () => {
    setAdditionalCosts(prev => [
      ...prev,
      { id: Date.now(), description: 'Other Cost', cost: 0 }
    ]);
  };

  const removeAdditionalCost = (id) => {
    setAdditionalCosts(prev => prev.filter(item => item.id !== id));
  };

  const updateAdditionalCost = (id, field, value) => {
    setAdditionalCosts(prev => prev.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const allocateResource = async () => {
    if (!newAllocation.employee_id || !newAllocation.start_date || !newAllocation.end_date) {
      toast.error('Employee and dates are required');
      return;
    }
    try {
      await api.post(`/project-management/jobs/${selectedJob.id}/team-resources/allocate`, newAllocation);
      toast.success('Resource allocated');
      setShowAllocateResource(false);
      setNewAllocation({ employee_id: '', start_date: '', end_date: '', hours_per_day: 8 });
      fetchTeamResources(selectedJob.id);
    } catch (error) {
      toast.error('Failed to allocate resource');
    }
  };

  const removeAllocation = async (allocationId) => {
    try {
      await api.delete(`/project-management/resource-allocations/${allocationId}`);
      toast.success('Allocation removed');
      fetchTeamResources(selectedJob.id);
    } catch (error) {
      toast.error('Failed to remove allocation');
    }
  };

  // ==================== Bid Template Functions ====================
  const fetchBidTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await api.get('/project-management/bid-templates');
      setBidTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching bid templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const saveBidTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Template name is required');
      return;
    }
    try {
      const templateData = {
        name: newTemplateName.trim(),
        description: newTemplateDescription.trim() || null,
        project_type: newTemplateProjectType || null,
        bid_type: bidType,
        labor_rates: bidData.laborRates,
        labor_line_items: laborLineItems.map(item => ({
          description: item.description,
          role: item.role,
          hours: item.hours,
          rate: item.rate
        })),
        material_markup: bidData.materialMarkup,
        overhead_percent: bidData.overheadPercent,
        profit_margin: bidData.profitMargin,
        contingency: bidData.contingency,
        additional_costs: additionalCosts.map(item => ({
          description: item.description,
          cost: item.cost
        }))
      };
      
      await api.post('/project-management/bid-templates', templateData);
      toast.success('Bid template saved successfully');
      setShowSaveTemplateModal(false);
      setNewTemplateName('');
      setNewTemplateDescription('');
      setNewTemplateProjectType('');
      fetchBidTemplates();
    } catch (error) {
      toast.error('Failed to save bid template');
    }
  };

  const loadBidTemplate = async (template) => {
    try {
      // Apply template to current bid calculator
      setBidType(template.bid_type || 'fixed_price');
      setBidData({
        laborRates: template.labor_rates || { lead: 75, journeyman: 55, apprentice: 35 },
        materialMarkup: template.material_markup || 20,
        overheadPercent: template.overhead_percent || 15,
        profitMargin: template.profit_margin || 20,
        contingency: template.contingency || 10
      });
      
      // Load labor line items
      if (template.labor_line_items && template.labor_line_items.length > 0) {
        setLaborLineItems(template.labor_line_items.map((item, idx) => ({
          id: idx + 1,
          description: item.description,
          role: item.role,
          hours: item.hours,
          rate: item.rate
        })));
      }
      
      // Load additional costs
      if (template.additional_costs && template.additional_costs.length > 0) {
        setAdditionalCosts(template.additional_costs.map((item, idx) => ({
          id: idx + 1,
          description: item.description,
          cost: item.cost
        })));
      }
      
      toast.success(`Template "${template.name}" loaded`);
      setShowLoadTemplateModal(false);
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const deleteBidTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/project-management/bid-templates/${templateId}`);
      toast.success('Template deleted');
      fetchBidTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const generateBidFromTemplate = async (templateId) => {
    if (!selectedJob) {
      toast.error('Please select a project first');
      return;
    }
    try {
      const response = await api.post(
        `/project-management/jobs/${selectedJob.id}/generate-bid?template_id=${templateId}`
      );
      const data = response.data;
      
      // Apply the generated bid config
      setBidType(data.bid_type);
      setBidData({
        laborRates: data.template_config.labor_rates,
        materialMarkup: data.template_config.material_markup,
        overheadPercent: data.template_config.overhead_percent,
        profitMargin: data.template_config.profit_margin,
        contingency: data.template_config.contingency
      });
      setLaborLineItems(data.template_config.labor_line_items.map((item, idx) => ({
        id: idx + 1,
        ...item
      })));
      setAdditionalCosts(data.template_config.additional_costs.map((item, idx) => ({
        id: idx + 1,
        ...item
      })));
      
      toast.success(`Bid generated: $${data.calculations.total_bid.toLocaleString()}`);
      setShowLoadTemplateModal(false);
    } catch (error) {
      toast.error('Failed to generate bid');
    }
  };

  // ==================== Task/Dependency Functions ====================
  const deleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await api.delete(`/project-management/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchTasks(selectedJob.id);
      fetchGanttData(selectedJob.id);
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const addDependency = async () => {
    if (!selectedTaskForDep || !newDependency.predecessor_id) {
      toast.error('Select a predecessor task');
      return;
    }
    try {
      await api.post(`/project-management/tasks/${selectedTaskForDep.id}/dependencies`, newDependency);
      toast.success('Dependency added');
      setShowAddDependency(false);
      setSelectedTaskForDep(null);
      setNewDependency({ predecessor_id: '', type: 'FS', lag_days: 0 });
      fetchTasks(selectedJob.id);
      fetchGanttData(selectedJob.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add dependency');
    }
  };

  const removeDependency = async (taskId, predecessorId) => {
    try {
      await api.delete(`/project-management/tasks/${taskId}/dependencies/${predecessorId}`);
      toast.success('Dependency removed');
      fetchTasks(selectedJob.id);
      fetchGanttData(selectedJob.id);
    } catch (error) {
      toast.error('Failed to remove dependency');
    }
  };

  const updateTaskFloatLag = async (taskId, floatDays, lagDays) => {
    try {
      await api.patch(`/project-management/tasks/${taskId}/float-lag`, {
        float_days: floatDays,
        lag_days: lagDays
      });
      toast.success('Float/Lag updated');
      fetchTasks(selectedJob.id);
      fetchGanttData(selectedJob.id);
    } catch (error) {
      toast.error('Failed to update float/lag');
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      await api.patch(`/project-management/tasks/${taskId}`, updates);
      toast.success('Task updated');
      setEditingTask(null);
      fetchTasks(selectedJob.id);
      fetchGanttData(selectedJob.id);
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  // Calculate bid totals
  const calculateBidTotals = (job) => {
    if (!job) return null;
    
    // Calculate labor from line items
    const laborCost = laborLineItems.reduce((sum, item) => sum + (item.hours * item.rate), 0);
    
    // Get material cost from BOM if available
    const bomMaterialCost = bomItems.reduce((sum, item) => sum + (item.total_cost || 0), 0);
    const materialCost = bomMaterialCost > 0 ? bomMaterialCost : (job.material_cost || 0);
    const materialWithMarkup = materialCost * (1 + bidData.materialMarkup / 100);
    
    // Additional costs
    const additionalCostsTotal = additionalCosts.reduce((sum, item) => sum + (item.cost || 0), 0);
    
    // Calculate totals
    const subtotal = laborCost + materialWithMarkup + additionalCostsTotal;
    const overhead = subtotal * (bidData.overheadPercent / 100);
    const profit = subtotal * (bidData.profitMargin / 100);
    const contingency = subtotal * (bidData.contingency / 100);
    const total = subtotal + overhead + profit + contingency;
    
    // T&M specific calculations
    const tmLaborTotal = laborLineItems.reduce((sum, item) => sum + (item.hours * item.rate), 0);
    const tmMarkupOnLabor = tmLaborTotal * (bidData.profitMargin / 100);
    
    return {
      laborCost,
      laborHours: laborLineItems.reduce((sum, item) => sum + item.hours, 0),
      materialCost,
      materialWithMarkup,
      additionalCosts: additionalCostsTotal,
      subtotal,
      overhead,
      profit,
      contingency,
      total,
      ebit: profit,
      // T&M specific
      tmLaborTotal,
      tmMaterialsTotal: materialWithMarkup,
      tmTotal: tmLaborTotal + tmMarkupOnLabor + materialWithMarkup + additionalCostsTotal,
      // Comparison
      fixedPriceTotal: total,
      savings: bidType === 'fixed_price' ? total - (tmLaborTotal + materialWithMarkup + additionalCostsTotal) : 0
    };
  };

  const filteredJobs = jobs.filter(job =>
    job.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.project_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ClipboardList },
    { id: 'bid-calculator', name: 'Bid Calculator', icon: Calculator },
    { id: 'team', name: 'Team & Resources', icon: Users },
    { id: 'materials', name: 'BOM / Materials', icon: Package },
    { id: 'documents', name: 'SOW / SOV', icon: FileText },
    { id: 'schedule', name: 'Gantt Schedule', icon: Calendar },
    { id: 'updates', name: 'Daily Updates', icon: RefreshCw },
    { id: 'alerts', name: 'Alerts', icon: Bell },
    { id: 'financials', name: 'Financials', icon: DollarSign },
    { id: 'settings', name: 'Visibility', icon: Settings }
  ];

  // Calculate Gantt bar position
  const calculateBarPosition = (task, projectStart, projectDuration) => {
    if (!task.start_date || !projectStart) return { left: 0, width: 10 };
    
    try {
      const start = new Date(task.start_date);
      const end = task.end_date ? new Date(task.end_date) : new Date(start.getTime() + 86400000);
      const projStart = new Date(projectStart);
      
      const daysFromStart = Math.max(0, (start - projStart) / 86400000);
      const duration = Math.max(1, (end - start) / 86400000);
      
      const left = (daysFromStart / projectDuration) * 100;
      const width = (duration / projectDuration) * 100;
      
      return { left: Math.min(left, 95), width: Math.min(width, 100 - left) };
    } catch {
      return { left: 0, width: 10 };
    }
  };

  if (!isCOO) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-management">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl">Project Management Suite</h1>
          <p className="text-gray-500 mt-1">Comprehensive bid building, scheduling, and project tracking</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#C6A87C] focus:outline-none"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Project List Sidebar */}
        <div className="lg:col-span-1 bg-white border border-gray-100 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-sm uppercase text-gray-600">Active Projects</h3>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {filteredJobs.map((job) => (
              <button
                key={job.id}
                onClick={() => setSelectedJob(job)}
                className={`w-full p-4 text-left border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                  selectedJob?.id === job.id ? 'bg-[#C6A87C]/10 border-l-4 border-l-[#C6A87C]' : ''
                }`}
              >
                <p className="font-medium text-sm">{job.client_name}</p>
                <p className="text-xs text-gray-500 mt-1">{job.project_type}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    job.status === 'active' ? 'bg-green-100 text-green-700' :
                    job.status === 'pursuing' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {job.status}
                  </span>
                  {job.progress > 0 && (
                    <span className="text-xs text-gray-500">{job.progress}%</span>
                  )}
                </div>
              </button>
            ))}
            {filteredJobs.length === 0 && (
              <div className="p-8 text-center text-gray-400">
                <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No projects found</p>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {selectedJob ? (
            <>
              {/* Project Header */}
              <div className="bg-white border border-gray-100 rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-heading">{selectedJob.client_name}</h2>
                    <p className="text-gray-500">{selectedJob.project_type} • {selectedJob.address}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#C6A87C]">
                      ${(calculateBidTotals(selectedJob)?.total || 0).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">Total Bid</p>
                  </div>
                </div>
                
                {/* Quick Stats */}
                <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-100">
                  <div className="text-center">
                    <p className="text-2xl font-bold">{ganttData?.progress_percent || selectedJob.progress || 0}%</p>
                    <p className="text-xs text-gray-500">Complete</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{selectedJob.team_members?.length || 0}</p>
                    <p className="text-xs text-gray-500">Team Size</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{ganttData?.total_duration_days || '--'}</p>
                    <p className="text-xs text-gray-500">Duration (days)</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">
                      ${(calculateBidTotals(selectedJob)?.ebit || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">EBIT</p>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="bg-white border border-gray-100 rounded-lg">
                <div className="flex overflow-x-auto border-b border-gray-100">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                        activeTab === tab.id
                          ? 'border-[#C6A87C] text-[#C6A87C]'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.name}
                    </button>
                  ))}
                </div>

                <div className="p-6">
                  {/* Overview Tab */}
                  {activeTab === 'overview' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold mb-3">Project Details</h4>
                          <dl className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <dt className="text-gray-500">Status</dt>
                              <dd className="font-medium capitalize">{selectedJob.status}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-gray-500">Start Date</dt>
                              <dd className="font-medium">{selectedJob.start_date || 'TBD'}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-gray-500">End Date</dt>
                              <dd className="font-medium">{selectedJob.end_date || 'TBD'}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-gray-500">Bid Type</dt>
                              <dd className="font-medium">{bidType === 'fixed_price' ? 'Fixed Price' : 'T&M'}</dd>
                            </div>
                          </dl>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-3">Progress Summary</h4>
                          <div className="space-y-3">
                            <div>
                              <div className="flex justify-between text-sm mb-1">
                                <span>Overall Progress</span>
                                <span className="font-medium">{ganttData?.progress_percent || 0}%</span>
                              </div>
                              <div className="h-2 bg-gray-200 rounded-full">
                                <div 
                                  className="h-full bg-[#C6A87C] rounded-full transition-all"
                                  style={{ width: `${ganttData?.progress_percent || 0}%` }}
                                />
                              </div>
                            </div>
                            <div className="text-sm">
                              <span className="text-gray-500">Tasks:</span>
                              <span className="ml-2 font-medium">{tasks.filter(t => t.status === 'completed').length} / {tasks.length} completed</span>
                            </div>
                            <div className="text-sm">
                              <span className="text-gray-500">Critical Path Tasks:</span>
                              <span className="ml-2 font-medium text-red-600">{ganttData?.critical_path?.length || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Bid Calculator Tab */}
                  {activeTab === 'bid-calculator' && (
                    <div className="space-y-6">
                      {/* Template Actions Bar */}
                      <div className="flex items-center justify-between bg-gradient-to-r from-[#0A0A0A] to-[#1a1a1a] border border-[#C6A87C]/30 rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <Calculator className="w-5 h-5 text-[#C6A87C]" />
                          <div>
                            <p className="font-medium text-white">Bid Templates</p>
                            <p className="text-xs text-gray-400">{bidTemplates.length} saved templates available</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setShowLoadTemplateModal(true)}
                            data-testid="load-template-btn"
                            className="px-4 py-2 bg-white/10 text-white rounded-lg text-sm flex items-center gap-2 hover:bg-white/20 transition-colors"
                          >
                            <FolderOpen className="w-4 h-4" /> Load Template
                          </button>
                          <button
                            onClick={() => setShowSaveTemplateModal(true)}
                            data-testid="save-template-btn"
                            className="px-4 py-2 bg-[#C6A87C] text-white rounded-lg text-sm flex items-center gap-2 hover:bg-[#B09265] transition-colors"
                          >
                            <Save className="w-4 h-4" /> Save as Template
                          </button>
                        </div>
                      </div>

                      {/* Bid Type Toggle */}
                      <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                        {BID_TYPES.map((type) => (
                          <button
                            key={type.id}
                            onClick={() => setBidType(type.id)}
                            className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                              bidType === type.id
                                ? 'border-[#C6A87C] bg-white'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <p className="font-semibold">{type.name}</p>
                            <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                          </button>
                        ))}
                      </div>

                      {/* Rate Source Toggle */}
                      <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div>
                          <p className="font-medium">Labor Rate Source</p>
                          <p className="text-sm text-gray-500">Pull rates from employee database or enter manually</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={syncRatesFromDb}
                            className="px-3 py-2 bg-blue-600 text-white rounded text-sm flex items-center gap-1 hover:bg-blue-700"
                          >
                            <RefreshCw className="w-4 h-4" /> Sync from DB
                          </button>
                        </div>
                      </div>

                      {/* Labor Line Items */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
                          <h4 className="font-semibold">Labor Costs</h4>
                          <button
                            onClick={addLaborLineItem}
                            className="px-3 py-1.5 bg-[#C6A87C] text-white rounded text-sm flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" /> Add Line
                          </button>
                        </div>
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Description</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Role</th>
                              <th className="px-4 py-2 text-right font-medium text-gray-600">Hours</th>
                              <th className="px-4 py-2 text-right font-medium text-gray-600">Rate/Hr</th>
                              <th className="px-4 py-2 text-right font-medium text-gray-600">Total</th>
                              <th className="px-4 py-2 text-right font-medium text-gray-600 w-16"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {laborLineItems.map((item) => (
                              <tr key={item.id}>
                                <td className="px-4 py-2">
                                  <input
                                    type="text"
                                    value={item.description}
                                    onChange={(e) => updateLaborLineItem(item.id, 'description', e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-200 rounded"
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <select
                                    value={item.role}
                                    onChange={(e) => {
                                      updateLaborLineItem(item.id, 'role', e.target.value);
                                      updateLaborLineItem(item.id, 'rate', bidData.laborRates[e.target.value]);
                                    }}
                                    className="px-2 py-1 border border-gray-200 rounded"
                                  >
                                    <option value="lead">Lead</option>
                                    <option value="journeyman">Journeyman</option>
                                    <option value="apprentice">Apprentice</option>
                                  </select>
                                </td>
                                <td className="px-4 py-2">
                                  <input
                                    type="number"
                                    value={item.hours}
                                    onChange={(e) => updateLaborLineItem(item.id, 'hours', Number(e.target.value))}
                                    className="w-20 px-2 py-1 border border-gray-200 rounded text-right"
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <div className="flex items-center justify-end">
                                    <span className="mr-1">$</span>
                                    <input
                                      type="number"
                                      value={item.rate}
                                      onChange={(e) => updateLaborLineItem(item.id, 'rate', Number(e.target.value))}
                                      className="w-20 px-2 py-1 border border-gray-200 rounded text-right"
                                    />
                                  </div>
                                </td>
                                <td className="px-4 py-2 text-right font-medium">
                                  ${(item.hours * item.rate).toLocaleString()}
                                </td>
                                <td className="px-4 py-2 text-right">
                                  <button
                                    onClick={() => removeLaborLineItem(item.id)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-gray-50 font-semibold">
                              <td colSpan="2" className="px-4 py-2 text-right">Total Labor:</td>
                              <td className="px-4 py-2 text-right">{laborLineItems.reduce((s, i) => s + i.hours, 0)} hrs</td>
                              <td></td>
                              <td className="px-4 py-2 text-right text-[#C6A87C]">
                                ${laborLineItems.reduce((s, i) => s + (i.hours * i.rate), 0).toLocaleString()}
                              </td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Materials Summary from BOM */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-semibold">Materials</h4>
                          {bomItems.length > 0 && (
                            <span className="text-sm text-green-600">Using BOM data ({bomItems.length} items)</span>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm text-gray-500">Base Cost</p>
                            <p className="text-xl font-bold">
                              ${(bomItems.length > 0 
                                ? bomItems.reduce((s, i) => s + (i.total_cost || 0), 0)
                                : (selectedJob?.material_cost || 0)
                              ).toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm text-gray-500 mb-1">Markup %</label>
                            <input
                              type="number"
                              value={bidData.materialMarkup}
                              onChange={(e) => setBidData({ ...bidData, materialMarkup: Number(e.target.value) })}
                              className="w-full px-3 py-2 border border-gray-200 rounded"
                            />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">With Markup</p>
                            <p className="text-xl font-bold text-[#C6A87C]">
                              ${(calculateBidTotals(selectedJob)?.materialWithMarkup || 0).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        {bomItems.length === 0 && (
                          <button
                            onClick={() => setActiveTab('materials')}
                            className="mt-3 text-[#C6A87C] text-sm flex items-center gap-1"
                          >
                            Add materials in BOM tab →
                          </button>
                        )}
                      </div>

                      {/* Additional Costs */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
                          <h4 className="font-semibold">Additional Costs</h4>
                          <button
                            onClick={addAdditionalCost}
                            className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-sm flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" /> Add
                          </button>
                        </div>
                        <div className="divide-y">
                          {additionalCosts.map((item) => (
                            <div key={item.id} className="flex items-center gap-4 p-3">
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) => updateAdditionalCost(item.id, 'description', e.target.value)}
                                className="flex-1 px-3 py-1.5 border border-gray-200 rounded"
                              />
                              <div className="flex items-center">
                                <span className="mr-1">$</span>
                                <input
                                  type="number"
                                  value={item.cost}
                                  onChange={(e) => updateAdditionalCost(item.id, 'cost', Number(e.target.value))}
                                  className="w-28 px-3 py-1.5 border border-gray-200 rounded text-right"
                                />
                              </div>
                              <button
                                onClick={() => removeAdditionalCost(item.id)}
                                className="text-red-500 hover:text-red-700 p-1"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                          <div className="bg-gray-50 px-4 py-3 flex justify-between font-semibold">
                            <span>Total Additional Costs:</span>
                            <span>${additionalCosts.reduce((s, i) => s + (i.cost || 0), 0).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Markup & Margin */}
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Overhead %</label>
                          <input
                            type="number"
                            value={bidData.overheadPercent}
                            onChange={(e) => setBidData({ ...bidData, overheadPercent: Number(e.target.value) })}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Profit Margin %</label>
                          <input
                            type="number"
                            value={bidData.profitMargin}
                            onChange={(e) => setBidData({ ...bidData, profitMargin: Number(e.target.value) })}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Contingency %</label>
                          <input
                            type="number"
                            value={bidData.contingency}
                            onChange={(e) => setBidData({ ...bidData, contingency: Number(e.target.value) })}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                          />
                        </div>
                        <div className="flex items-end">
                          <div className="w-full bg-gray-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-gray-500">Total Markup</p>
                            <p className="text-lg font-bold">{bidData.overheadPercent + bidData.profitMargin + bidData.contingency}%</p>
                          </div>
                        </div>
                      </div>

                      {/* Bid Comparison - T&M vs Fixed */}
                      <div className="grid grid-cols-2 gap-6">
                        {/* Fixed Price Summary */}
                        <div className={`rounded-lg p-6 ${bidType === 'fixed_price' ? 'bg-[#0A0A0A] text-white' : 'bg-gray-50'}`}>
                          <h4 className={`text-lg font-semibold mb-4 ${bidType === 'fixed_price' ? 'text-[#C6A87C]' : ''}`}>
                            Fixed Price Bid
                            {bidType === 'fixed_price' && <span className="ml-2 text-xs bg-[#C6A87C] text-white px-2 py-1 rounded">SELECTED</span>}
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className={bidType === 'fixed_price' ? 'text-gray-400' : 'text-gray-600'}>Labor</span>
                              <span>${(calculateBidTotals(selectedJob)?.laborCost || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className={bidType === 'fixed_price' ? 'text-gray-400' : 'text-gray-600'}>Materials (w/ markup)</span>
                              <span>${(calculateBidTotals(selectedJob)?.materialWithMarkup || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className={bidType === 'fixed_price' ? 'text-gray-400' : 'text-gray-600'}>Additional Costs</span>
                              <span>${(calculateBidTotals(selectedJob)?.additionalCosts || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between border-t pt-2 mt-2">
                              <span className={bidType === 'fixed_price' ? 'text-gray-400' : 'text-gray-600'}>Subtotal</span>
                              <span>${(calculateBidTotals(selectedJob)?.subtotal || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className={bidType === 'fixed_price' ? 'text-gray-400' : 'text-gray-600'}>Overhead ({bidData.overheadPercent}%)</span>
                              <span>${(calculateBidTotals(selectedJob)?.overhead || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className={bidType === 'fixed_price' ? 'text-gray-400' : 'text-gray-600'}>Profit ({bidData.profitMargin}%)</span>
                              <span className="text-green-400">${(calculateBidTotals(selectedJob)?.profit || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className={bidType === 'fixed_price' ? 'text-gray-400' : 'text-gray-600'}>Contingency ({bidData.contingency}%)</span>
                              <span>${(calculateBidTotals(selectedJob)?.contingency || 0).toLocaleString()}</span>
                            </div>
                            <div className={`flex justify-between text-xl font-bold pt-3 border-t mt-3 ${bidType === 'fixed_price' ? 'text-[#C6A87C] border-white/20' : 'border-gray-300'}`}>
                              <span>Total Bid</span>
                              <span>${(calculateBidTotals(selectedJob)?.total || 0).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>

                        {/* T&M Summary */}
                        <div className={`rounded-lg p-6 ${bidType === 'time_material' ? 'bg-[#0A0A0A] text-white' : 'bg-gray-50'}`}>
                          <h4 className={`text-lg font-semibold mb-4 ${bidType === 'time_material' ? 'text-[#C6A87C]' : ''}`}>
                            Time & Material Bid
                            {bidType === 'time_material' && <span className="ml-2 text-xs bg-[#C6A87C] text-white px-2 py-1 rounded">SELECTED</span>}
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className={bidType === 'time_material' ? 'text-gray-400' : 'text-gray-600'}>
                                Labor ({calculateBidTotals(selectedJob)?.laborHours || 0} hrs)
                              </span>
                              <span>${(calculateBidTotals(selectedJob)?.laborCost || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className={bidType === 'time_material' ? 'text-gray-400' : 'text-gray-600'}>Materials (w/ markup)</span>
                              <span>${(calculateBidTotals(selectedJob)?.materialWithMarkup || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className={bidType === 'time_material' ? 'text-gray-400' : 'text-gray-600'}>Additional Costs</span>
                              <span>${(calculateBidTotals(selectedJob)?.additionalCosts || 0).toLocaleString()}</span>
                            </div>
                            <div className={`flex justify-between pt-3 border-t mt-3 ${bidType === 'time_material' ? 'border-white/20' : 'border-gray-300'}`}>
                              <span className={bidType === 'time_material' ? 'text-gray-400' : 'text-gray-600'}>Base T&M Total</span>
                              <span className="font-medium">
                                ${((calculateBidTotals(selectedJob)?.laborCost || 0) + 
                                   (calculateBidTotals(selectedJob)?.materialWithMarkup || 0) +
                                   (calculateBidTotals(selectedJob)?.additionalCosts || 0)).toLocaleString()}
                              </span>
                            </div>
                            <div className="mt-4 p-3 bg-yellow-500/10 rounded text-yellow-600 text-xs">
                              <strong>Note:</strong> T&M final cost will vary based on actual hours and materials used. 
                              Client is billed for actual work performed.
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Final Recommendation */}
                      <div className="bg-gradient-to-r from-[#C6A87C]/20 to-[#C6A87C]/5 border border-[#C6A87C]/30 rounded-lg p-6">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="text-lg font-semibold">Recommended Bid Price</h4>
                            <p className="text-sm text-gray-600 mt-1">
                              {bidType === 'fixed_price' 
                                ? 'Fixed price with built-in margins and contingency'
                                : 'T&M estimate based on projected hours (final may vary)'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-3xl font-bold text-[#C6A87C]">
                              ${(calculateBidTotals(selectedJob)?.total || 0).toLocaleString()}
                            </p>
                            <p className="text-sm text-green-600 mt-1">
                              Est. Profit: ${(calculateBidTotals(selectedJob)?.profit || 0).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Save Template Modal */}
                      {showSaveTemplateModal && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
                            <div className="flex justify-between items-center mb-4">
                              <h3 className="text-lg font-semibold">Save Bid Template</h3>
                              <button onClick={() => setShowSaveTemplateModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium mb-1">Template Name *</label>
                                <input
                                  type="text"
                                  value={newTemplateName}
                                  onChange={(e) => setNewTemplateName(e.target.value)}
                                  placeholder="e.g., Kitchen Remodel Standard"
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                                  data-testid="template-name-input"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Description</label>
                                <textarea
                                  value={newTemplateDescription}
                                  onChange={(e) => setNewTemplateDescription(e.target.value)}
                                  placeholder="Brief description of this template..."
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                                  rows={2}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium mb-1">Project Type</label>
                                <select
                                  value={newTemplateProjectType}
                                  onChange={(e) => setNewTemplateProjectType(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                                >
                                  <option value="">Any Project Type</option>
                                  <option value="Kitchen">Kitchen Remodel</option>
                                  <option value="Bathroom">Bathroom Remodel</option>
                                  <option value="Full Remodel">Full Remodel</option>
                                  <option value="Addition">Addition</option>
                                  <option value="Flooring">Flooring</option>
                                  <option value="Painting">Painting</option>
                                  <option value="Roofing">Roofing</option>
                                  <option value="Other">Other</option>
                                </select>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                                <p className="font-medium mb-2">Template will include:</p>
                                <ul className="text-gray-600 space-y-1">
                                  <li>• Bid type: {bidType === 'fixed_price' ? 'Fixed Price' : 'Time & Material'}</li>
                                  <li>• {laborLineItems.length} labor line items</li>
                                  <li>• Markup: {bidData.overheadPercent}% overhead, {bidData.profitMargin}% profit, {bidData.contingency}% contingency</li>
                                  <li>• {additionalCosts.length} additional cost items</li>
                                </ul>
                              </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                              <button
                                onClick={() => setShowSaveTemplateModal(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={saveBidTemplate}
                                data-testid="save-template-confirm-btn"
                                className="px-4 py-2 bg-[#C6A87C] text-white rounded-lg hover:bg-[#B09265]"
                              >
                                Save Template
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Load Template Modal */}
                      {showLoadTemplateModal && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                          <div className="bg-white rounded-lg p-6 w-full max-w-2xl shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
                            <div className="flex justify-between items-center mb-4">
                              <h3 className="text-lg font-semibold">Load Bid Template</h3>
                              <button onClick={() => setShowLoadTemplateModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                            
                            {loadingTemplates ? (
                              <div className="flex items-center justify-center py-12">
                                <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
                              </div>
                            ) : bidTemplates.length === 0 ? (
                              <div className="text-center py-12">
                                <FileDown className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                                <p className="text-gray-500">No templates saved yet</p>
                                <p className="text-sm text-gray-400 mt-1">Configure a bid and save it as a template</p>
                              </div>
                            ) : (
                              <div className="overflow-y-auto flex-1 -mx-6 px-6">
                                <div className="space-y-3">
                                  {bidTemplates.map((template) => (
                                    <div
                                      key={template.id}
                                      className="border border-gray-200 rounded-lg p-4 hover:border-[#C6A87C] transition-colors"
                                    >
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <h4 className="font-semibold">{template.name}</h4>
                                            {template.project_type && (
                                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                                {template.project_type}
                                              </span>
                                            )}
                                            <span className={`px-2 py-0.5 text-xs rounded ${
                                              template.bid_type === 'fixed_price' 
                                                ? 'bg-blue-100 text-blue-700' 
                                                : 'bg-green-100 text-green-700'
                                            }`}>
                                              {template.bid_type === 'fixed_price' ? 'Fixed Price' : 'T&M'}
                                            </span>
                                          </div>
                                          {template.description && (
                                            <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                                          )}
                                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                            <span>{template.labor_line_items?.length || 0} labor items</span>
                                            <span>Markup: {template.overhead_percent + template.profit_margin + template.contingency}%</span>
                                            <span>Created: {new Date(template.created_at).toLocaleDateString()}</span>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <button
                                            onClick={() => loadBidTemplate(template)}
                                            data-testid={`load-template-${template.id}`}
                                            className="px-3 py-1.5 bg-[#C6A87C] text-white rounded text-sm hover:bg-[#B09265] flex items-center gap-1"
                                          >
                                            <Copy className="w-3 h-3" /> Load
                                          </button>
                                          <button
                                            onClick={() => generateBidFromTemplate(template.id)}
                                            data-testid={`generate-bid-${template.id}`}
                                            className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center gap-1"
                                          >
                                            <Zap className="w-3 h-3" /> Generate Bid
                                          </button>
                                          <button
                                            onClick={() => deleteBidTemplate(template.id)}
                                            className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <div className="flex justify-end mt-4 pt-4 border-t">
                              <button
                                onClick={() => setShowLoadTemplateModal(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                              >
                                Close
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Team Tab */}
                  {activeTab === 'team' && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold">Team & Resource Allocation</h4>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => setShowAllocateResource(true)}
                            className="px-4 py-2 bg-[#C6A87C] text-white rounded-lg text-sm hover:bg-[#B09265] flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" /> Allocate Resource
                          </button>
                          <button 
                            onClick={() => navigate('/portal/team')}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                          >
                            Team Builder
                          </button>
                        </div>
                      </div>

                      {/* Allocate Resource Modal */}
                      {showAllocateResource && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                          <div className="flex justify-between items-center">
                            <h5 className="font-semibold">Allocate Resource to Project</h5>
                            <button onClick={() => setShowAllocateResource(false)} className="text-gray-400 hover:text-gray-600">
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="col-span-2">
                              <label className="block text-sm font-medium mb-1">Employee</label>
                              <select
                                value={newAllocation.employee_id}
                                onChange={(e) => setNewAllocation({ ...newAllocation, employee_id: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                              >
                                <option value="">Select employee...</option>
                                {employees.map((emp) => (
                                  <option key={emp.id} value={emp.id}>
                                    {emp.name} - {emp.role} (${emp.hourly_rate || 0}/hr)
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Start Date</label>
                              <input
                                type="date"
                                value={newAllocation.start_date}
                                onChange={(e) => setNewAllocation({ ...newAllocation, start_date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">End Date</label>
                              <input
                                type="date"
                                value={newAllocation.end_date}
                                onChange={(e) => setNewAllocation({ ...newAllocation, end_date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Hours/Day</label>
                              <input
                                type="number"
                                value={newAllocation.hours_per_day}
                                onChange={(e) => setNewAllocation({ ...newAllocation, hours_per_day: Number(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={allocateResource} className="px-4 py-2 bg-[#C6A87C] text-white rounded text-sm">
                              Allocate
                            </button>
                            <button onClick={() => setShowAllocateResource(false)} className="px-4 py-2 bg-gray-200 rounded text-sm">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Team Members */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b">
                          <h5 className="font-medium">Assigned Team ({teamResources.team?.length || 0})</h5>
                        </div>
                        {teamResources.team?.length > 0 ? (
                          <div className="divide-y">
                            {teamResources.team.map((member) => (
                              <div key={member.id} className="p-4 flex justify-between items-center">
                                <div>
                                  <p className="font-medium">{member.name}</p>
                                  <p className="text-sm text-gray-500">{member.role} • {member.contractor_id}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-medium">${member.hourly_rate || 0}/hr</p>
                                  <p className="text-xs text-gray-500">${member.daily_rate || 0}/day</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-8 text-center">
                            <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                            <p className="text-gray-500">No team assigned yet</p>
                          </div>
                        )}
                      </div>

                      {/* Resource Allocations */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b">
                          <h5 className="font-medium">Resource Schedule ({teamResources.allocations?.length || 0})</h5>
                        </div>
                        {teamResources.allocations?.length > 0 ? (
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b">
                              <tr>
                                <th className="px-4 py-2 text-left">Employee</th>
                                <th className="px-4 py-2 text-left">Dates</th>
                                <th className="px-4 py-2 text-left">Hours/Day</th>
                                <th className="px-4 py-2 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {teamResources.allocations.map((alloc) => (
                                <tr key={alloc.id}>
                                  <td className="px-4 py-3">{alloc.employee_name}</td>
                                  <td className="px-4 py-3">{alloc.start_date} to {alloc.end_date}</td>
                                  <td className="px-4 py-3">{alloc.hours_per_day}h</td>
                                  <td className="px-4 py-3 text-right">
                                    <button
                                      onClick={() => removeAllocation(alloc.id)}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="p-6 text-center text-gray-500">
                            No allocations scheduled
                          </div>
                        )}
                      </div>

                      {/* Labor Cost Summary */}
                      {employeeRates && (
                        <div className="bg-[#0A0A0A] text-white rounded-lg p-6">
                          <h5 className="text-[#C6A87C] font-semibold mb-4">Average Labor Rates (From Database)</h5>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <p className="text-gray-400 text-sm">Lead Average</p>
                              <p className="text-xl font-bold">${employeeRates.average_rates?.lead?.toFixed(2) || 0}/hr</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-sm">Journeyman Average</p>
                              <p className="text-xl font-bold">${employeeRates.average_rates?.journeyman?.toFixed(2) || 0}/hr</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-sm">Apprentice Average</p>
                              <p className="text-xl font-bold">${employeeRates.average_rates?.apprentice?.toFixed(2) || 0}/hr</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Materials/BOM Tab */}
                  {activeTab === 'materials' && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold">Bill of Materials (BOM)</h4>
                        <button 
                          onClick={() => setShowAddBomItem(true)}
                          className="px-4 py-2 bg-[#C6A87C] text-white rounded-lg text-sm hover:bg-[#B09265] flex items-center gap-1"
                        >
                          <Plus className="w-4 h-4" /> Add Material
                        </button>
                      </div>

                      {/* Add BOM Item Modal */}
                      {showAddBomItem && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                          <div className="flex justify-between items-center">
                            <h5 className="font-semibold">Add Material to BOM</h5>
                            <button onClick={() => setShowAddBomItem(false)} className="text-gray-400 hover:text-gray-600">
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="col-span-2">
                              <label className="block text-sm font-medium mb-1">Item Name *</label>
                              <input
                                type="text"
                                value={newBomItem.name}
                                onChange={(e) => setNewBomItem({ ...newBomItem, name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                                placeholder="2x4x8 Stud"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Category</label>
                              <select
                                value={newBomItem.category}
                                onChange={(e) => setNewBomItem({ ...newBomItem, category: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                              >
                                {bomCategories.map((cat) => (
                                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Quantity *</label>
                              <input
                                type="number"
                                value={newBomItem.quantity}
                                onChange={(e) => setNewBomItem({ ...newBomItem, quantity: Number(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Unit</label>
                              <select
                                value={newBomItem.unit}
                                onChange={(e) => setNewBomItem({ ...newBomItem, unit: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                              >
                                <option value="each">Each</option>
                                <option value="sqft">Sq Ft</option>
                                <option value="linear_ft">Linear Ft</option>
                                <option value="box">Box</option>
                                <option value="bundle">Bundle</option>
                                <option value="gallon">Gallon</option>
                                <option value="roll">Roll</option>
                                <option value="sheet">Sheet</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Unit Cost ($) *</label>
                              <input
                                type="number"
                                step="0.01"
                                value={newBomItem.unit_cost}
                                onChange={(e) => setNewBomItem({ ...newBomItem, unit_cost: Number(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Vendor</label>
                              <input
                                type="text"
                                value={newBomItem.vendor}
                                onChange={(e) => setNewBomItem({ ...newBomItem, vendor: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                                placeholder="Home Depot"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Part Number</label>
                              <input
                                type="text"
                                value={newBomItem.part_number}
                                onChange={(e) => setNewBomItem({ ...newBomItem, part_number: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-sm font-medium mb-1">Notes</label>
                              <input
                                type="text"
                                value={newBomItem.notes}
                                onChange={(e) => setNewBomItem({ ...newBomItem, notes: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={addBomItem} className="px-4 py-2 bg-[#C6A87C] text-white rounded text-sm">
                              Add Material
                            </button>
                            <button onClick={() => setShowAddBomItem(false)} className="px-4 py-2 bg-gray-200 rounded text-sm">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* BOM Table */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left font-medium text-gray-600">Item</th>
                              <th className="px-4 py-3 text-left font-medium text-gray-600">Category</th>
                              <th className="px-4 py-3 text-right font-medium text-gray-600">Qty</th>
                              <th className="px-4 py-3 text-right font-medium text-gray-600">Unit Cost</th>
                              <th className="px-4 py-3 text-right font-medium text-gray-600">Total</th>
                              <th className="px-4 py-3 text-center font-medium text-gray-600">Status</th>
                              <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bomItems.length > 0 ? (
                              bomItems.map((item) => (
                                <tr key={item.id} className="border-t hover:bg-gray-50">
                                  <td className="px-4 py-3">
                                    <p className="font-medium">{item.name}</p>
                                    {item.vendor && <p className="text-xs text-gray-500">{item.vendor}</p>}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600">
                                    {bomCategories.find(c => c.id === item.category)?.name || item.category}
                                  </td>
                                  <td className="px-4 py-3 text-right">{item.quantity} {item.unit}</td>
                                  <td className="px-4 py-3 text-right">${item.unit_cost?.toFixed(2)}</td>
                                  <td className="px-4 py-3 text-right font-medium">${item.total_cost?.toFixed(2)}</td>
                                  <td className="px-4 py-3 text-center">
                                    <select
                                      value={item.status}
                                      onChange={(e) => updateBomStatus(item.id, e.target.value)}
                                      className={`text-xs px-2 py-1 rounded border ${
                                        item.status === 'received' ? 'bg-green-100 text-green-700' :
                                        item.status === 'ordered' ? 'bg-blue-100 text-blue-700' :
                                        item.status === 'installed' ? 'bg-purple-100 text-purple-700' :
                                        'bg-gray-100'
                                      }`}
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="ordered">Ordered</option>
                                      <option value="received">Received</option>
                                      <option value="installed">Installed</option>
                                    </select>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <button
                                      onClick={() => deleteBomItem(item.id)}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr className="border-t">
                                <td className="px-4 py-8 text-gray-500 italic text-center" colSpan="7">
                                  No materials added yet. Add items to build your BOM.
                                </td>
                              </tr>
                            )}
                          </tbody>
                          {bomItems.length > 0 && (
                            <tfoot className="bg-gray-50 border-t">
                              <tr>
                                <td colSpan="4" className="px-4 py-3 text-right font-semibold">Total:</td>
                                <td className="px-4 py-3 text-right font-bold text-lg">
                                  ${bomItems.reduce((sum, item) => sum + (item.total_cost || 0), 0).toFixed(2)}
                                </td>
                                <td colSpan="2"></td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>

                      {/* BOM Summary by Category */}
                      {bomItems.length > 0 && (
                        <div className="bg-[#0A0A0A] text-white rounded-lg p-6">
                          <h5 className="text-[#C6A87C] font-semibold mb-4">Cost Summary by Category</h5>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {Object.entries(
                              bomItems.reduce((acc, item) => {
                                acc[item.category] = (acc[item.category] || 0) + (item.total_cost || 0);
                                return acc;
                              }, {})
                            ).map(([cat, total]) => (
                              <div key={cat}>
                                <p className="text-gray-400 text-sm">
                                  {bomCategories.find(c => c.id === cat)?.name || cat}
                                </p>
                                <p className="text-lg font-bold">${total.toFixed(2)}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Documents Tab - SOW & SOV */}
                  {activeTab === 'documents' && (
                    <div className="space-y-6">
                      {/* Scope of Work Section */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
                          <h4 className="font-semibold flex items-center gap-2">
                            <FileText className="w-5 h-5 text-[#C6A87C]" />
                            Scope of Work (SOW)
                          </h4>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setShowAddSowSection(true)}
                              className="px-3 py-1.5 bg-[#C6A87C] text-white rounded text-sm flex items-center gap-1"
                            >
                              <Plus className="w-4 h-4" /> Add Section
                            </button>
                            <button
                              onClick={saveSow}
                              className="px-3 py-1.5 bg-green-600 text-white rounded text-sm flex items-center gap-1"
                            >
                              <Save className="w-4 h-4" /> Save SOW
                            </button>
                          </div>
                        </div>
                        
                        {showAddSowSection && (
                          <div className="p-4 bg-gray-50 border-b space-y-3">
                            <input
                              type="text"
                              placeholder="Section Title"
                              value={newSowSection.title}
                              onChange={(e) => setNewSowSection({ ...newSowSection, title: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-200 rounded"
                            />
                            <textarea
                              placeholder="Section content..."
                              value={newSowSection.content}
                              onChange={(e) => setNewSowSection({ ...newSowSection, content: e.target.value })}
                              rows={4}
                              className="w-full px-3 py-2 border border-gray-200 rounded"
                            />
                            <div className="flex gap-2">
                              <button onClick={addSowSection} className="px-4 py-2 bg-[#C6A87C] text-white rounded text-sm">
                                Add Section
                              </button>
                              <button onClick={() => setShowAddSowSection(false)} className="px-4 py-2 bg-gray-200 rounded text-sm">
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                        
                        <div className="divide-y">
                          {sowData.sections?.length > 0 ? (
                            sowData.sections.map((section, idx) => (
                              <div key={idx} className="p-4">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <h5 className="font-medium">{section.title}</h5>
                                    <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{section.content}</p>
                                  </div>
                                  <button
                                    onClick={() => {
                                      setSowData(prev => ({
                                        ...prev,
                                        sections: prev.sections.filter((_, i) => i !== idx)
                                      }));
                                    }}
                                    className="text-red-500 hover:text-red-700 ml-2"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-8 text-center text-gray-500">
                              No scope sections defined. Add sections to build your SOW.
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Schedule of Values Section */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
                          <h4 className="font-semibold flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-[#C6A87C]" />
                            Schedule of Values (SOV)
                          </h4>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setShowAddSovItem(true)}
                              className="px-3 py-1.5 bg-[#C6A87C] text-white rounded text-sm flex items-center gap-1"
                            >
                              <Plus className="w-4 h-4" /> Add Line Item
                            </button>
                            <button
                              onClick={saveSov}
                              className="px-3 py-1.5 bg-green-600 text-white rounded text-sm flex items-center gap-1"
                            >
                              <Save className="w-4 h-4" /> Save SOV
                            </button>
                          </div>
                        </div>

                        {showAddSovItem && (
                          <div className="p-4 bg-gray-50 border-b grid grid-cols-3 gap-3">
                            <input
                              type="text"
                              placeholder="Description"
                              value={newSovItem.description}
                              onChange={(e) => setNewSovItem({ ...newSovItem, description: e.target.value })}
                              className="col-span-2 px-3 py-2 border border-gray-200 rounded"
                            />
                            <input
                              type="number"
                              placeholder="Scheduled Value ($)"
                              value={newSovItem.scheduled_value || ''}
                              onChange={(e) => setNewSovItem({ ...newSovItem, scheduled_value: Number(e.target.value) })}
                              className="px-3 py-2 border border-gray-200 rounded"
                            />
                            <div className="col-span-3 flex gap-2">
                              <button onClick={addSovItem} className="px-4 py-2 bg-[#C6A87C] text-white rounded text-sm">
                                Add Line Item
                              </button>
                              <button onClick={() => setShowAddSovItem(false)} className="px-4 py-2 bg-gray-200 rounded text-sm">
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="px-4 py-3 text-left font-medium text-gray-600">#</th>
                              <th className="px-4 py-3 text-left font-medium text-gray-600">Description</th>
                              <th className="px-4 py-3 text-right font-medium text-gray-600">Scheduled Value</th>
                              <th className="px-4 py-3 text-center font-medium text-gray-600">% Complete</th>
                              <th className="px-4 py-3 text-right font-medium text-gray-600">Completed Value</th>
                              <th className="px-4 py-3 text-right font-medium text-gray-600">Retainage</th>
                              <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sovData.line_items?.length > 0 ? (
                              sovData.line_items.map((item, idx) => (
                                <tr key={item.id || idx} className="border-t hover:bg-gray-50">
                                  <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                                  <td className="px-4 py-3">{item.description}</td>
                                  <td className="px-4 py-3 text-right">${item.scheduled_value?.toLocaleString()}</td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={item.percent_complete || 0}
                                        onChange={(e) => {
                                          const newItems = [...sovData.line_items];
                                          newItems[idx].percent_complete = Number(e.target.value);
                                          newItems[idx].completed_value = item.scheduled_value * Number(e.target.value) / 100;
                                          setSovData({ ...sovData, line_items: newItems });
                                        }}
                                        className="w-16"
                                      />
                                      <span className="text-sm font-medium">{item.percent_complete || 0}%</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-right font-medium">
                                    ${(item.completed_value || 0).toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-right text-gray-500">
                                    ${((item.completed_value || 0) * 0.1).toLocaleString()}
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <button
                                      onClick={() => {
                                        setSovData(prev => ({
                                          ...prev,
                                          line_items: prev.line_items.filter((_, i) => i !== idx)
                                        }));
                                      }}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                                  No line items. Add items to build your Schedule of Values.
                                </td>
                              </tr>
                            )}
                          </tbody>
                          {sovData.line_items?.length > 0 && (
                            <tfoot className="bg-[#0A0A0A] text-white">
                              <tr>
                                <td colSpan="2" className="px-4 py-3 font-semibold">TOTALS</td>
                                <td className="px-4 py-3 text-right font-bold">
                                  ${sovData.line_items.reduce((s, i) => s + (i.scheduled_value || 0), 0).toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {Math.round(
                                    sovData.line_items.reduce((s, i) => s + (i.completed_value || 0), 0) /
                                    sovData.line_items.reduce((s, i) => s + (i.scheduled_value || 0), 0) * 100
                                  ) || 0}%
                                </td>
                                <td className="px-4 py-3 text-right font-bold text-green-400">
                                  ${sovData.line_items.reduce((s, i) => s + (i.completed_value || 0), 0).toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-right text-[#C6A87C]">
                                  ${(sovData.line_items.reduce((s, i) => s + (i.completed_value || 0), 0) * 0.1).toLocaleString()}
                                </td>
                                <td></td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Schedule/Gantt Tab */}
                  {activeTab === 'schedule' && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold">Project Schedule</h4>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setShowAddTask(true)}
                            className="px-3 py-1.5 text-sm bg-[#C6A87C] text-white rounded flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" /> Add Task
                          </button>
                          <button
                            onClick={() => setGanttViewMode('simple')}
                            className={`px-3 py-1.5 text-sm rounded ${
                              ganttViewMode === 'simple' ? 'bg-[#C6A87C] text-white' : 'bg-gray-100'
                            }`}
                          >
                            Simple
                          </button>
                          <button
                            onClick={() => setGanttViewMode('detailed')}
                            className={`px-3 py-1.5 text-sm rounded ${
                              ganttViewMode === 'detailed' ? 'bg-[#C6A87C] text-white' : 'bg-gray-100'
                            }`}
                          >
                            Detailed
                          </button>
                        </div>
                      </div>
                      
                      {/* Add Task Modal */}
                      {showAddTask && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                          <h5 className="font-semibold">New Task</h5>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <input
                              type="text"
                              placeholder="Task name"
                              value={newTask.name}
                              onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                              className="px-3 py-2 border border-gray-200 rounded col-span-2"
                            />
                            <input
                              type="number"
                              placeholder="Estimated hours"
                              value={newTask.estimated_hours}
                              onChange={(e) => setNewTask({ ...newTask, estimated_hours: Number(e.target.value) })}
                              className="px-3 py-2 border border-gray-200 rounded"
                            />
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={newTask.is_milestone}
                                onChange={(e) => setNewTask({ ...newTask, is_milestone: e.target.checked })}
                                id="is-milestone"
                              />
                              <label htmlFor="is-milestone" className="text-sm">Milestone</label>
                            </div>
                            <input
                              type="date"
                              placeholder="Start date"
                              value={newTask.start_date}
                              onChange={(e) => setNewTask({ ...newTask, start_date: e.target.value })}
                              className="px-3 py-2 border border-gray-200 rounded"
                            />
                            <input
                              type="date"
                              placeholder="End date"
                              value={newTask.end_date}
                              onChange={(e) => setNewTask({ ...newTask, end_date: e.target.value })}
                              className="px-3 py-2 border border-gray-200 rounded"
                            />
                            <textarea
                              placeholder="Description (optional)"
                              value={newTask.description || ''}
                              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                              className="px-3 py-2 border border-gray-200 rounded col-span-2"
                              rows={1}
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={createTask}
                              className="px-4 py-2 bg-[#C6A87C] text-white rounded text-sm"
                            >
                              Create Task
                            </button>
                            <button
                              onClick={() => setShowAddTask(false)}
                              className="px-4 py-2 bg-gray-200 rounded text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Add Dependency Modal */}
                      {showAddDependency && selectedTaskForDep && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                          <div className="flex justify-between items-center">
                            <h5 className="font-semibold">Add Dependency to: {selectedTaskForDep.name}</h5>
                            <button onClick={() => { setShowAddDependency(false); setSelectedTaskForDep(null); }}>
                              <X className="w-5 h-5 text-gray-500" />
                            </button>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-1">Predecessor Task</label>
                              <select
                                value={newDependency.predecessor_id}
                                onChange={(e) => setNewDependency({ ...newDependency, predecessor_id: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                              >
                                <option value="">Select task...</option>
                                {tasks.filter(t => t.id !== selectedTaskForDep.id).map(t => (
                                  <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Dependency Type</label>
                              <select
                                value={newDependency.type}
                                onChange={(e) => setNewDependency({ ...newDependency, type: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                              >
                                <option value="FS">Finish-to-Start (FS)</option>
                                <option value="SS">Start-to-Start (SS)</option>
                                <option value="FF">Finish-to-Finish (FF)</option>
                                <option value="SF">Start-to-Finish (SF)</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Lag (days)</label>
                              <input
                                type="number"
                                value={newDependency.lag_days}
                                onChange={(e) => setNewDependency({ ...newDependency, lag_days: Number(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-200 rounded"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={addDependency} className="px-4 py-2 bg-blue-600 text-white rounded text-sm flex items-center gap-1">
                              <Link className="w-4 h-4" /> Link Tasks
                            </button>
                            <button onClick={() => { setShowAddDependency(false); setSelectedTaskForDep(null); }} className="px-4 py-2 bg-gray-200 rounded text-sm">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Task List with Full Management */}
                      {tasks.length > 0 && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Task</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Dates</th>
                                <th className="px-3 py-2 text-center font-medium text-gray-600">Progress</th>
                                <th className="px-3 py-2 text-center font-medium text-gray-600">Float</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Dependencies</th>
                                <th className="px-3 py-2 text-center font-medium text-gray-600">Status</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-600">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {tasks.map((task) => (
                                <tr key={task.id} className={`hover:bg-gray-50 ${task.is_critical_path ? 'bg-red-50' : ''}`}>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      {task.is_milestone && <span className="text-[#C6A87C]">◆</span>}
                                      <span className={`font-medium ${task.is_critical_path ? 'text-red-600' : ''}`}>
                                        {task.name}
                                      </span>
                                      {task.is_critical_path && (
                                        <span className="text-xs bg-red-100 text-red-600 px-1 rounded">Critical</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-xs">
                                    {task.start_date} → {task.end_date}
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                        <div 
                                          className="h-full bg-[#C6A87C]" 
                                          style={{ width: `${task.progress_percent || 0}%` }}
                                        />
                                      </div>
                                      <span className="text-xs">{task.progress_percent || 0}%</span>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-center text-xs">
                                    {task.float_days?.toFixed(1) || 0}d
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="flex flex-wrap gap-1">
                                      {(task.dependencies || []).map((dep, idx) => {
                                        const depId = typeof dep === 'string' ? dep : dep?.predecessor_id;
                                        const depTask = tasks.find(t => t.id === depId);
                                        const depType = typeof dep === 'object' ? dep.type : 'FS';
                                        return depTask ? (
                                          <span 
                                            key={idx} 
                                            className="inline-flex items-center gap-1 text-xs bg-gray-100 px-2 py-0.5 rounded group"
                                          >
                                            {depTask.name.slice(0, 15)}... ({depType})
                                            <button
                                              onClick={() => removeDependency(task.id, depId)}
                                              className="text-red-500 opacity-0 group-hover:opacity-100"
                                            >
                                              <X className="w-3 h-3" />
                                            </button>
                                          </span>
                                        ) : null;
                                      })}
                                      <button
                                        onClick={() => { setSelectedTaskForDep(task); setShowAddDependency(true); }}
                                        className="text-xs text-blue-600 hover:text-blue-800"
                                      >
                                        + Add
                                      </button>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2">
                                    <select
                                      value={task.status}
                                      onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                                      className={`text-xs px-2 py-1 rounded border w-full ${
                                        task.status === 'completed' ? 'bg-green-100 text-green-700' :
                                        task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                        'bg-gray-100'
                                      }`}
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="in_progress">In Progress</option>
                                      <option value="completed">Completed</option>
                                    </select>
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <button
                                        onClick={() => setEditingTask(task)}
                                        className="p-1 text-gray-500 hover:text-[#C6A87C]"
                                        title="Edit task"
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => deleteTask(task.id)}
                                        className="p-1 text-gray-500 hover:text-red-500"
                                        title="Delete task"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Edit Task Modal */}
                      {editingTask && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                          <div className="bg-white rounded-lg w-full max-w-lg p-6 space-y-4">
                            <div className="flex justify-between items-center">
                              <h5 className="font-semibold text-lg">Edit Task</h5>
                              <button onClick={() => setEditingTask(null)}>
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium mb-1">Task Name</label>
                                <input
                                  type="text"
                                  value={editingTask.name}
                                  onChange={(e) => setEditingTask({ ...editingTask, name: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-200 rounded"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium mb-1">Start Date</label>
                                  <input
                                    type="date"
                                    value={editingTask.start_date || ''}
                                    onChange={(e) => setEditingTask({ ...editingTask, start_date: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-1">End Date</label>
                                  <input
                                    type="date"
                                    value={editingTask.end_date || ''}
                                    onChange={(e) => setEditingTask({ ...editingTask, end_date: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-sm font-medium mb-1">Est. Hours</label>
                                  <input
                                    type="number"
                                    value={editingTask.estimated_hours || 0}
                                    onChange={(e) => setEditingTask({ ...editingTask, estimated_hours: Number(e.target.value) })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-1">Progress %</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={editingTask.progress_percent || 0}
                                    onChange={(e) => setEditingTask({ ...editingTask, progress_percent: Number(e.target.value) })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-1">Float (days)</label>
                                  <input
                                    type="number"
                                    value={editingTask.float_days || 0}
                                    onChange={(e) => setEditingTask({ ...editingTask, float_days: Number(e.target.value) })}
                                    className="w-full px-3 py-2 border border-gray-200 rounded"
                                  />
                                </div>
                              </div>
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={editingTask.is_milestone || false}
                                  onChange={(e) => setEditingTask({ ...editingTask, is_milestone: e.target.checked })}
                                />
                                <span className="text-sm">Milestone</span>
                              </label>
                            </div>
                            <div className="flex gap-2 pt-4 border-t">
                              <button
                                onClick={() => updateTask(editingTask.id, editingTask)}
                                className="px-4 py-2 bg-[#C6A87C] text-white rounded text-sm flex items-center gap-1"
                              >
                                <Save className="w-4 h-4" /> Save Changes
                              </button>
                              <button
                                onClick={() => setEditingTask(null)}
                                className="px-4 py-2 bg-gray-200 rounded text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Gantt Chart Visual */}
                      <div className="border border-gray-200 rounded-lg bg-gray-50 min-h-[300px] overflow-x-auto">
                        {tasks.length > 0 ? (
                          <div className="p-4">
                            {/* Timeline header */}
                            <div className="flex items-center gap-4 mb-4 border-b pb-2">
                              <div className="w-40 font-semibold text-sm">Task</div>
                              <div className="flex-1 relative h-6">
                                <div className="absolute left-0 text-xs text-gray-500">
                                  {ganttData?.start_date}
                                </div>
                                <div className="absolute right-0 text-xs text-gray-500">
                                  {ganttData?.end_date}
                                </div>
                              </div>
                            </div>
                            
                            {/* Task bars */}
                            <div className="space-y-2">
                              {tasks.map((task) => {
                                const pos = calculateBarPosition(
                                  task, 
                                  ganttData?.start_date, 
                                  ganttData?.total_duration_days || 30
                                );
                                return (
                                  <div key={task.id} className="flex items-center gap-4">
                                    <div className="w-40 text-sm truncate flex items-center gap-1" title={task.name}>
                                      {task.is_milestone && <span className="text-[#C6A87C]">◆</span>}
                                      <span className={task.is_critical_path ? 'text-red-600 font-medium' : ''}>
                                        {task.name}
                                      </span>
                                    </div>
                                    <div className="flex-1 h-7 bg-gray-200 rounded relative">
                                      <div 
                                        className={`absolute h-full rounded transition-all cursor-pointer hover:opacity-80 ${
                                          task.is_critical_path ? 'bg-red-500' : 'bg-[#C6A87C]'
                                        }`}
                                        style={{ 
                                          left: `${pos.left}%`, 
                                          width: `${Math.max(pos.width, 2)}%`
                                        }}
                                        onClick={() => setEditingTask(task)}
                                        title={`${task.name}: ${task.progress_percent || 0}% complete`}
                                      >
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <span className="text-xs text-white font-medium">
                                            {task.progress_percent || 0}%
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            
                            {/* Summary */}
                            <div className="mt-6 pt-4 border-t border-gray-200">
                              <div className="grid grid-cols-4 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">Total Float:</span>
                                  <span className="ml-2 font-medium">{ganttData?.total_float?.toFixed(1) || 0} days</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Duration:</span>
                                  <span className="ml-2 font-medium">{ganttData?.total_duration_days || 0} days</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Critical Path Tasks:</span>
                                  <span className="ml-2 font-medium text-red-600">{ganttData?.critical_path?.length || 0}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Milestones:</span>
                                  <span className="ml-2 font-medium">{ganttData?.milestones?.length || 0}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="p-8 text-center">
                            <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                            <p className="text-gray-500 mb-4">No tasks yet. Start building your project schedule.</p>
                            <button 
                              onClick={() => setShowAddTask(true)}
                              className="px-4 py-2 bg-[#C6A87C] text-white rounded text-sm"
                            >
                              Add First Task
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Legend */}
                      <div className="flex gap-6 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-[#C6A87C] rounded" />
                          <span>Regular Task</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-red-500 rounded" />
                          <span>Critical Path</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[#C6A87C]">◆</span>
                          <span>Milestone</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Link2 className="w-4 h-4 text-blue-500" />
                          <span>FS=Finish-to-Start, SS=Start-to-Start, FF=Finish-to-Finish</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Daily Updates Tab */}
                  {activeTab === 'updates' && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold">Daily Updates</h4>
                        <button
                          onClick={() => fetchDailyUpdates(selectedJob.id)}
                          className="text-sm text-[#C6A87C] flex items-center gap-1"
                        >
                          <RefreshCw className="w-4 h-4" /> Refresh
                        </button>
                      </div>
                      
                      {loadingUpdates ? (
                        <div className="p-8 text-center">
                          <div className="w-6 h-6 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin mx-auto" />
                        </div>
                      ) : dailyUpdates.length > 0 ? (
                        <div className="space-y-4">
                          {dailyUpdates.map((update) => (
                            <div key={update.id} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex justify-between items-start mb-3">
                                <div>
                                  <p className="font-medium">{update.submitted_by_name}</p>
                                  <p className="text-sm text-gray-500">{update.submission_date}</p>
                                </div>
                                <div className="text-right">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-500">Progress:</span>
                                    <span className="font-medium">{update.project_progress_before}%</span>
                                    <ArrowRight className="w-4 h-4 text-gray-400" />
                                    <span className="font-medium text-green-600">{update.project_progress_after}%</span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Tasks completed */}
                              {update.tasks_completed?.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-sm font-medium text-gray-600 mb-1">Tasks:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {update.tasks_completed.map((task, idx) => (
                                      <span 
                                        key={idx}
                                        className={`px-2 py-1 text-xs rounded ${
                                          task.is_completed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                        }`}
                                      >
                                        {task.task_name} ({task.progress_percent}%)
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {update.general_notes && (
                                <p className="text-sm text-gray-600 mb-2">
                                  <span className="font-medium">Notes:</span> {update.general_notes}
                                </p>
                              )}
                              
                              {update.issues_blockers && (
                                <p className="text-sm text-red-600 mb-2">
                                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                                  <span className="font-medium">Issues:</span> {update.issues_blockers}
                                </p>
                              )}
                              
                              {update.photos?.length > 0 && (
                                <div className="flex gap-2 mt-2">
                                  <Camera className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm text-gray-500">{update.photos.length} photo(s)</span>
                                </div>
                              )}
                              
                              {update.crew_names?.length > 0 && (
                                <p className="text-sm text-gray-500 mt-2">
                                  <Users className="w-4 h-4 inline mr-1" />
                                  Crew: {update.crew_names.join(', ')}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-8 text-center bg-gray-50 rounded-lg">
                          <RefreshCw className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                          <p className="text-gray-500">No daily updates yet</p>
                          <p className="text-sm text-gray-400 mt-1">Team leads can submit updates from the Employee Portal</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Alerts Tab */}
                  {activeTab === 'alerts' && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold">Alert Settings</h4>
                        <button
                          onClick={sendUpdateReminder}
                          className="px-4 py-2 bg-[#C6A87C] text-white rounded-lg text-sm hover:bg-[#B09265] flex items-center gap-2"
                        >
                          <Send className="w-4 h-4" /> Send Reminder Now
                        </button>
                      </div>
                      
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                        <h5 className="font-medium">Daily Update Reminder</h5>
                        
                        <div className="space-y-3">
                          <label className="flex items-center gap-3">
                            <input 
                              type="checkbox" 
                              defaultChecked 
                              className="w-4 h-4 text-[#C6A87C] rounded"
                            />
                            <span className="text-sm">Send daily update reminder</span>
                          </label>
                          
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2">
                              <input 
                                type="checkbox" 
                                defaultChecked 
                                className="w-4 h-4 text-[#C6A87C] rounded"
                              />
                              <Bell className="w-4 h-4 text-gray-500" />
                              <span className="text-sm">In-App</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input 
                                type="checkbox" 
                                defaultChecked 
                                className="w-4 h-4 text-[#C6A87C] rounded"
                              />
                              <Mail className="w-4 h-4 text-gray-500" />
                              <span className="text-sm">Email</span>
                            </label>
                          </div>
                          
                          <div>
                            <label className="block text-sm text-gray-600 mb-2">Reminder Time</label>
                            <input 
                              type="time" 
                              defaultValue="17:00"
                              className="px-3 py-2 border border-gray-200 rounded"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                        <h5 className="font-medium">Recipients (Team Leads / Foremen)</h5>
                        
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {employees.filter(e => e.role === 'lead' || e.role === 'foreman' || e.expertise_level === 'master').map((emp) => (
                            <label key={emp.id} className="flex items-center gap-3 p-2 hover:bg-white rounded">
                              <input
                                type="checkbox"
                                checked={alertRecipients.includes(emp.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setAlertRecipients([...alertRecipients, emp.id]);
                                  } else {
                                    setAlertRecipients(alertRecipients.filter(id => id !== emp.id));
                                  }
                                }}
                                className="w-4 h-4 text-[#C6A87C] rounded"
                              />
                              <span className="text-sm">{emp.name}</span>
                              <span className="text-xs text-gray-400">{emp.role || emp.expertise_level}</span>
                            </label>
                          ))}
                          {employees.filter(e => e.role === 'lead' || e.role === 'foreman' || e.expertise_level === 'master').length === 0 && (
                            <p className="text-sm text-gray-500 p-2">No team leads or foremen found</p>
                          )}
                        </div>
                        
                        <button
                          onClick={saveAlertSettings}
                          className="px-4 py-2 bg-[#C6A87C] text-white rounded text-sm"
                        >
                          Save Settings
                        </button>
                      </div>
                      
                      {alertSettings?.last_alert_sent && (
                        <p className="text-sm text-gray-500">
                          Last reminder sent: {new Date(alertSettings.last_alert_sent).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Financials Tab */}
                  {activeTab === 'financials' && (
                    <div className="space-y-6">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <p className="text-sm text-green-600">Total Revenue</p>
                          <p className="text-2xl font-bold text-green-700">
                            ${(financials?.total_revenue || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-sm text-red-600">Total Expenses</p>
                          <p className="text-2xl font-bold text-red-700">
                            ${(financials?.total_expenses || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p className="text-sm text-blue-600">Net Profit</p>
                          <p className="text-2xl font-bold text-blue-700">
                            ${(financials?.net_profit || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <p className="text-sm text-purple-600">Profit Margin</p>
                          <p className="text-2xl font-bold text-purple-700">
                            {financials?.profit_margin || 0}%
                          </p>
                        </div>
                      </div>

                      {/* Tax Deductible Info */}
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-yellow-700 font-medium">Tax Deductible Expenses</p>
                            <p className="text-xl font-bold text-yellow-800">
                              ${(financials?.tax_deductible_expenses || 0).toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-yellow-700 font-medium">Pending Approval</p>
                            <p className="text-xl font-bold text-yellow-800">
                              ${(financials?.pending_expenses || 0).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Revenue Section */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-semibold flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-600" />
                            Revenue
                          </h4>
                          {isCOO && (
                            <button
                              onClick={() => setShowAddRevenue(true)}
                              className="px-3 py-1.5 bg-green-600 text-white rounded text-sm flex items-center gap-1"
                            >
                              <Plus className="w-4 h-4" /> Add Revenue
                            </button>
                          )}
                        </div>

                        {showAddRevenue && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <input
                                type="text"
                                placeholder="Description"
                                value={newRevenue.description}
                                onChange={(e) => setNewRevenue({...newRevenue, description: e.target.value})}
                                className="px-3 py-2 border border-gray-200 rounded"
                              />
                              <input
                                type="number"
                                placeholder="Amount"
                                value={newRevenue.amount}
                                onChange={(e) => setNewRevenue({...newRevenue, amount: e.target.value})}
                                className="px-3 py-2 border border-gray-200 rounded"
                              />
                              <select
                                value={newRevenue.payment_type}
                                onChange={(e) => setNewRevenue({...newRevenue, payment_type: e.target.value})}
                                className="px-3 py-2 border border-gray-200 rounded"
                              >
                                <option value="deposit">Deposit</option>
                                <option value="progress_payment">Progress Payment</option>
                                <option value="final_payment">Final Payment</option>
                                <option value="change_order">Change Order</option>
                              </select>
                              <input
                                type="date"
                                value={newRevenue.payment_date}
                                onChange={(e) => setNewRevenue({...newRevenue, payment_date: e.target.value})}
                                className="px-3 py-2 border border-gray-200 rounded"
                              />
                            </div>
                            <input
                              type="text"
                              placeholder="Invoice Number (optional)"
                              value={newRevenue.invoice_number}
                              onChange={(e) => setNewRevenue({...newRevenue, invoice_number: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-200 rounded"
                            />
                            <div className="flex gap-2">
                              <button onClick={submitRevenue} className="px-4 py-2 bg-green-600 text-white rounded text-sm">
                                Save Revenue
                              </button>
                              <button onClick={() => setShowAddRevenue(false)} className="px-4 py-2 bg-gray-200 rounded text-sm">
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        {revenue.length > 0 ? (
                          <div className="space-y-2">
                            {revenue.map((rev) => (
                              <div key={rev.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                                <div>
                                  <p className="font-medium text-sm">{rev.description}</p>
                                  <p className="text-xs text-gray-500">{rev.payment_date} • {rev.payment_type}</p>
                                </div>
                                <p className="font-bold text-green-600">${rev.amount.toLocaleString()}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No revenue recorded yet</p>
                        )}
                      </div>

                      {/* Expenses Section */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-semibold flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-red-600" />
                            Expenses (with Receipts)
                          </h4>
                          <button
                            onClick={() => setShowAddExpense(true)}
                            className="px-3 py-1.5 bg-[#C6A87C] text-white rounded text-sm flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" /> Add Expense
                          </button>
                        </div>

                        {showAddExpense && (
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <select
                                value={newExpense.category}
                                onChange={(e) => setNewExpense({...newExpense, category: e.target.value})}
                                className="px-3 py-2 border border-gray-200 rounded"
                              >
                                {expenseCategories.map((cat) => (
                                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                              </select>
                              <input
                                type="number"
                                placeholder="Amount"
                                value={newExpense.amount}
                                onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
                                className="px-3 py-2 border border-gray-200 rounded"
                              />
                              <input
                                type="text"
                                placeholder="Description"
                                value={newExpense.description}
                                onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                                className="col-span-2 px-3 py-2 border border-gray-200 rounded"
                              />
                              <input
                                type="text"
                                placeholder="Vendor"
                                value={newExpense.vendor}
                                onChange={(e) => setNewExpense({...newExpense, vendor: e.target.value})}
                                className="px-3 py-2 border border-gray-200 rounded"
                              />
                              <input
                                type="date"
                                value={newExpense.expense_date}
                                onChange={(e) => setNewExpense({...newExpense, expense_date: e.target.value})}
                                className="px-3 py-2 border border-gray-200 rounded"
                              />
                            </div>
                            
                            {/* Receipt Upload */}
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                              <div className="text-center">
                                <Camera className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                                <label className="cursor-pointer">
                                  <span className="text-sm text-[#C6A87C] font-medium">Upload Receipt Photo</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleReceiptUpload}
                                    className="hidden"
                                  />
                                </label>
                                <p className="text-xs text-gray-400 mt-1">JPEG, PNG • Max 5MB each</p>
                              </div>
                              {newExpense.receipt_photos.length > 0 && (
                                <div className="flex gap-2 mt-3 flex-wrap">
                                  {newExpense.receipt_photos.map((photo, idx) => (
                                    <div key={idx} className="relative">
                                      <img src={photo} alt={`Receipt ${idx + 1}`} className="w-20 h-20 object-cover rounded" />
                                      <button
                                        onClick={() => setNewExpense({
                                          ...newExpense,
                                          receipt_photos: newExpense.receipt_photos.filter((_, i) => i !== idx)
                                        })}
                                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="flex gap-4">
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={newExpense.is_billable}
                                  onChange={(e) => setNewExpense({...newExpense, is_billable: e.target.checked})}
                                />
                                Billable
                              </label>
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={newExpense.tax_deductible}
                                  onChange={(e) => setNewExpense({...newExpense, tax_deductible: e.target.checked})}
                                />
                                Tax Deductible
                              </label>
                            </div>

                            <textarea
                              placeholder="Notes (optional)"
                              value={newExpense.notes}
                              onChange={(e) => setNewExpense({...newExpense, notes: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-200 rounded text-sm"
                              rows="2"
                            />

                            <div className="flex gap-2">
                              <button onClick={submitExpense} className="px-4 py-2 bg-[#C6A87C] text-white rounded text-sm">
                                Submit Expense
                              </button>
                              <button onClick={() => setShowAddExpense(false)} className="px-4 py-2 bg-gray-200 rounded text-sm">
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        {expenses.length > 0 ? (
                          <div className="space-y-3">
                            {expenses.map((exp) => (
                              <div key={exp.id} className="p-4 bg-gray-50 rounded-lg">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-sm">{exp.description}</p>
                                      <span className={`px-2 py-0.5 text-xs rounded ${
                                        exp.status === 'approved' ? 'bg-green-100 text-green-700' :
                                        exp.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                        'bg-yellow-100 text-yellow-700'
                                      }`}>
                                        {exp.status}
                                      </span>
                                      {exp.tax_deductible && (
                                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                          Tax Deductible
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {exp.category_display} • {exp.vendor || 'No vendor'} • {exp.expense_date}
                                    </p>
                                    <p className="text-xs text-gray-400">
                                      Submitted by {exp.submitted_by_name}
                                    </p>
                                  </div>
                                  <p className="font-bold text-red-600">${exp.amount.toLocaleString()}</p>
                                </div>

                                {/* Receipt Images */}
                                {exp.receipt_urls?.length > 0 && (
                                  <div className="flex gap-2 mt-3">
                                    {exp.receipt_urls.map((url, idx) => (
                                      <a
                                        key={idx}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block"
                                      >
                                        <img
                                          src={url}
                                          alt={`Receipt ${idx + 1} for ${exp.description}`}
                                          className="w-16 h-16 object-cover rounded border border-gray-200 hover:border-[#C6A87C]"
                                        />
                                      </a>
                                    ))}
                                  </div>
                                )}

                                {/* Approval buttons for COO */}
                                {isCOO && exp.status === 'pending' && (
                                  <div className="flex gap-2 mt-3">
                                    <button
                                      onClick={() => approveExpense(exp.id, true)}
                                      className="px-3 py-1 bg-green-600 text-white rounded text-xs"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => approveExpense(exp.id, false)}
                                      className="px-3 py-1 bg-red-600 text-white rounded text-xs"
                                    >
                                      Reject
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-400">
                            <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No expenses recorded yet</p>
                            <p className="text-xs mt-1">Take a photo of receipts to submit expenses</p>
                          </div>
                        )}
                      </div>

                      {/* Expenses by Category */}
                      {financials?.expenses_by_category && Object.keys(financials.expenses_by_category).length > 0 && (
                        <div className="border border-gray-200 rounded-lg p-4">
                          <h4 className="font-semibold mb-4">Expenses by Category</h4>
                          <div className="space-y-2">
                            {Object.entries(financials.expenses_by_category).map(([cat, amount]) => (
                              <div key={cat} className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">{cat}</span>
                                <span className="font-medium">${amount.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Visibility Settings Tab */}
                  {activeTab === 'settings' && (
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-semibold mb-4 flex items-center gap-2">
                          <Eye className="w-5 h-5" /> Walter (President) Visibility
                        </h4>
                        <div className="space-y-3">
                          {Object.entries(visibilitySettings.walter).map(([key, value]) => (
                            <label key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm">{key.replace(/([A-Z])/g, ' $1').replace('can View', 'Can View').replace('can ', 'Can ')}</span>
                              <button
                                onClick={() => setVisibilitySettings({
                                  ...visibilitySettings,
                                  walter: { ...visibilitySettings.walter, [key]: !value }
                                })}
                                className={`w-12 h-6 rounded-full transition-colors ${value ? 'bg-[#C6A87C]' : 'bg-gray-300'}`}
                              >
                                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${value ? 'translate-x-6' : 'translate-x-0.5'}`} />
                              </button>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold mb-4 flex items-center gap-2">
                          <Users className="w-5 h-5" /> Team Leads / Foremen Visibility
                        </h4>
                        <div className="space-y-3">
                          {Object.entries(visibilitySettings.teamLeads).map(([key, value]) => (
                            <label key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm">{key.replace(/([A-Z])/g, ' $1').replace('can View', 'Can View').replace('can Submit', 'Can Submit').replace('can ', 'Can ')}</span>
                              <button
                                onClick={() => setVisibilitySettings({
                                  ...visibilitySettings,
                                  teamLeads: { ...visibilitySettings.teamLeads, [key]: !value }
                                })}
                                className={`w-12 h-6 rounded-full transition-colors ${value ? 'bg-[#C6A87C]' : 'bg-gray-300'}`}
                              >
                                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${value ? 'translate-x-6' : 'translate-x-0.5'}`} />
                              </button>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white border border-gray-100 rounded-lg p-12 text-center">
              <ClipboardList className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-heading mb-2">Select a Project</h3>
              <p className="text-gray-500">Choose a project from the list to view its management details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectManagement;
